import { useId, type ReactNode } from "react";
import clsx from "clsx";
import fieldStyles from "../Field/Field.module.scss";
import { Picker, type PickerOption } from "../Picker/Picker.js";
import { Spinner } from "../Spinner/Spinner.js";
import { Tag, type TagSize, type TagTone } from "../Tag/Tag.js";
import styles from "./ChipPicker.module.scss";

/** One thing already chosen. `label` is PLAIN TEXT — it names the remove button; `render` is the
 *  glyph-capable chip body (defaults to the label). */
export interface ChipPickerChip {
  value: string;
  label: string;
  render?: ReactNode;
  /** Secondary text rendered lighter after the label (`Tag`'s `meta`) — a drop date, a count. */
  meta?: ReactNode;
}

export interface ChipPickerProps {
  /** Accessible name for the type-ahead control, and the field's visible label. */
  label: string;
  /** The candidates. The CALLER filters out what is already chosen — this component does not know how to
   *  compare a chip to an option beyond its value, and a caller that wants repeats should get them. */
  options: PickerOption[];
  chips: ChipPickerChip[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  placeholder?: string;
  /** Swap the control for a spinner while the candidate list is in flight. Chips still render — they are
   *  the caller's state, not the query's. */
  loading?: boolean;
  loadingLabel?: string;
  /** A consequence line under the control. Omit it where the label already says everything. */
  hint?: ReactNode;
  /**
   * How many chips may be held. Reaching it HIDES the control rather than disabling it: a type-ahead you
   * may not type in is a dead field, and for the common `max={1}` case the chip IS the value — removing it
   * is how you change your mind. Unbounded by default.
   *
   * The field stays NAMED either way — see the label note on the component.
   */
  max?: number;
  disabled?: boolean;
  /** Read the label to screen readers only — for a field whose surrounding heading already names it. The
   *  label stays in the DOM in both states (with and without the control), so the chips stay named. */
  hideLabel?: boolean;
  chipTone?: TagTone;
  chipSize?: TagSize;
  className?: string;
}

/**
 * A type-ahead that builds a SELECTION as removable chips: pick from the list, the choice drops below as a
 * `Tag`, the input returns to its placeholder for the next one.
 *
 * Extracted 2026-08-23 from `CrewInviteePicker`, which had been the only one of these; the release link
 * picker on the task create modal is the second, and it differs only in what it fetches, how a row is drawn
 * and that it holds ONE chip. Everything else — the stack, the loading swap, the ADD-trigger driving of
 * `Picker`, the chip list, the hint line — was going to be copied verbatim, which is what this exists to
 * stop.
 *
 * **It is an ADD trigger, not a select.** The underlying `Picker`'s `value` is always empty, so it never
 * shows a "current" choice of its own: a pick appends a chip and the field resets. That is what makes the
 * same control serve one-of and many-of without branching — `max` is the only difference.
 *
 * **The label survives the control.** `Picker` carries it on its `FieldShell`, so hiding the control at
 * `max` would take the field's name with it and leave a chip floating under whatever field precedes it —
 * the one state where the reader most needs to be told what the chip IS. This renders it directly in that
 * case, in `FieldShell`'s own treatment and at the same `space-2` rhythm, and points the chip list at it
 * with `aria-labelledby` so the name reaches AT too (a bare `<label>` with no control to name would not).
 *
 * Presentational by construction (the ui-package boundary): every candidate, every chip and every label is
 * the caller's. The data-aware wrappers (`CrewInviteePicker`, `ReleasePicker`) live app-side.
 */
export function ChipPicker({
  label,
  options,
  chips,
  onAdd,
  onRemove,
  placeholder,
  loading = false,
  loadingLabel = "Loading…",
  hint,
  max,
  disabled = false,
  hideLabel = false,
  chipTone,
  chipSize,
  className,
}: ChipPickerProps) {
  const full = max !== undefined && chips.length >= max;
  const labelId = useId();

  return (
    <div className={clsx(styles.picker, className)}>
      {/* Only when the control is gone — otherwise `Picker`'s FieldShell renders it and this would double. */}
      {full && (
        <span id={labelId} className={hideLabel ? fieldStyles.srOnly : fieldStyles.label}>
          {label}
        </span>
      )}

      {loading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={16} label={loadingLabel} />
        </div>
      ) : (
        !full && (
          <Picker
            label={label}
            hideLabel={hideLabel}
            typeahead
            value=""
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(value) => {
              if (value) onAdd(value);
            }}
          />
        )
      )}

      {chips.length > 0 && (
        <ul className={styles.chips} aria-labelledby={full ? labelId : undefined}>
          {chips.map((chip) => (
            <li key={chip.value}>
              <Tag
                tone={chipTone}
                size={chipSize}
                meta={chip.meta}
                removeLabel={chip.label}
                onRemove={disabled ? undefined : () => onRemove(chip.value)}
              >
                {chip.render ?? chip.label}
              </Tag>
            </li>
          ))}
        </ul>
      )}

      {hint && <p className={styles.hint}>{hint}</p>}
    </div>
  );
}
